import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Text,
  Flex,
  toast,
} from '@kognitos/lattice';
import {
  getListAutomationsQueryKey,
  getListAutomationsInfiniteQueryKey,
  getListAutomationSummariesQueryKey,
  getListAutomationSummariesInfiniteQueryKey,
  getGetAutomationQueryKey,
  getQueryAutomationQueryKey,
  useActivateAutomation,
} from '@/generated/hooks/grimoire/v1/api';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

interface IAutomationReactivateDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  automationId: string;
  onReactivate?: () => void;
}

export function AutomationReactivateDialog({
  open,
  onClose,
  title,
  automationId,
  onReactivate,
}: IAutomationReactivateDialogProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { mutate: activateAutomation, isPending: isReactivatePending } =
    useActivateAutomation();

  const handleClose = () => {
    onClose();
  };

  const handleReactivate = () => {
    activateAutomation(
      {
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: automationId,
        data: {},
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getListAutomationsQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationsInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getGetAutomationQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              automationId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getQueryAutomationQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              automationId,
            ),
          });
          toast.success(
            tAutomation('automation.reactivateConfirmation.success'),
          );
          handleClose();
          onReactivate?.();
        },
        onError: () => {
          toast.error(tAutomation('automation.reactivateConfirmation.error'));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="automation-reactivate-dialog" stopPropagation>
        <DialogHeader>
          <DialogTitle>
            {tAutomation('automation.reactivateConfirmation.title')}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription asChild>
          <Flex direction="column" gap={4}>
            <Text color="muted" level="small">
              {tAutomation('automation.reactivateConfirmation.description', {
                processTitle: title,
              })}
            </Text>
          </Flex>
        </DialogDescription>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            data-testid="automation-reactivate-dialog-cancel"
          >
            {tCommon('common.action.cancel')}
          </Button>
          <Button
            onClick={handleReactivate}
            disabled={isReactivatePending}
            isLoading={isReactivatePending}
            data-testid="automation-reactivate-dialog-confirm"
          >
            {tAutomation(
              'automation.reactivateConfirmation.actions.reactivate',
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
